import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { msalInstance } from '@/providers/msal'

// All callers of acquireToken (GraphQL errorLink, axios interceptor, bootstrap hook) run
// non-interactively, so concurrent 401/UNAUTHORIZED responses can each trigger their own MSAL
// call at once. Dedupe them behind a single in-flight promise.
let inFlightAcquireToken: Promise<string | null> | null = null

export const acquireToken = (loginRequest: SilentRequest): Promise<string | null> => {
  // skip inside MSAL's own popup or an iframe (hidden silent-renew frame)
  const isMsalPopup = window.opener != null && window.name.startsWith('msal.')
  if (isMsalPopup || window.self !== window.top) {
    return Promise.resolve(null)
  }

  if (!inFlightAcquireToken) {
    inFlightAcquireToken = acquireTokenSilent(loginRequest)
      .catch(handleTokenAcquisitionError)
      .finally(() => {
        inFlightAcquireToken = null
      })
  }

  return inFlightAcquireToken
}

// attempt to acquire token silently
const acquireTokenSilent = async (loginRequest: SilentRequest): Promise<string> => {
  const tokenSilent = await msalInstance.acquireTokenSilent(loginRequest)
  return tokenSilent.idToken
}

// `null` means MSAL confirmed the session truly needs interactive re-auth (loginRedirect on the
// login page). Any other error (e.g. a transient `monitor_window_timeout`) is rethrown so callers
// treat it as a failed attempt, not a confirmed "log the user out" signal. We no longer fall back
// to an automatic popup here - every caller runs non-interactively, and browsers reliably block
// popups that aren't opened from a user gesture, so that fallback was silently failing and
// forcing a hard logout even when the real Azure AD session was still valid.
const handleTokenAcquisitionError = (error: any): string | null => {
  if (error instanceof InteractionRequiredAuthError) {
    return null
  }

  throw error
}
